import { useEffect, useState, useCallback } from 'react';
import { api, getApiErrorMessage, ApiError } from '@/services/api';
import CircularGauge from '@/components/base/CircularGauge';
import InverterGroupCard from './InverterGroupCard';
import type { InverterTelemetryResponse, InverterItem } from './inverterTelemetryTypes';
import { inverterTelemetryMock } from '@/mocks/inverterTelemetry';
import { useTheme } from '@/hooks/useTheme';

interface Props {
  assetId: string;
}

const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
const MAX_INVERTER_KW = 125;

const STATE_COLORS: Record<string, string> = {
  generating: '#0D7A3C',
  standby_or_waiting: '#9B9BA4',
  normal_no_power: '#6B6B70',
};

const STATE_LABELS: Record<string, string> = {
  generating: 'Generating',
  standby_or_waiting: 'Standby',
  normal_no_power: 'No Power',
};

function fmtKw(v: number | null): string {
  if (v == null) return '—';
  return v.toFixed(1);
}

function fmtPct(v: number | null): string {
  if (v == null) return '—';
  return `${v.toFixed(1)}%`;
}

function fmtDelta(v: number | null, pct: number | null): string {
  if (v == null || pct == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)} kW (${sign}${pct.toFixed(1)}%)`;
}

function fmtTime(ts: string | null): string {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    return ts;
  }
}

function getGaugeColor(inv: InverterItem): string {
  if (inv.qualityFlag === 'no_data' || inv.qualityFlag.startsWith('read_error') || inv.qualityFlag.startsWith('invalid_')) {
    return '#D12229';
  }
  return STATE_COLORS[inv.operationalState] ?? '#6B6B70';
}

function isMuted(inv: InverterItem): boolean {
  return inv.qualityFlag === 'no_data' || inv.pacKw == null;
}

function InverterCard({ inv, isDark }: { inv: InverterItem; isDark: boolean }) {
  const color = getGaugeColor(inv);
  const muted = isMuted(inv);
  const stateLabel = STATE_LABELS[inv.operationalState] ?? inv.operationalState;
  const cardBg = isDark ? '#1A1A1A' : '#FAFAFA';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#F0F0F2';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#6B6B70' : '#6B6B70';

  return (
    <div
      className="relative flex flex-col items-center rounded-xl p-4 transition-all"
      style={{
        background: cardBg,
        border: `1px solid ${cardBorder}`,
      }}
    >
      {/* Quality flag badge */}
      {inv.qualityFlag !== 'ok' && (
        <span
          className="absolute top-2 right-2 text-[10px] font-semibold px-1.5 py-0.5 rounded"
          style={{
            background: inv.qualityFlag === 'no_data' ? (isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2') : 'rgba(209,34,41,0.08)',
            color: inv.qualityFlag === 'no_data' ? '#9B9BA4' : '#D12229',
          }}
        >
          {inv.qualityFlag}
        </span>
      )}

      <div className="mt-3">
        <CircularGauge
          value={inv.pacKw}
          max={MAX_INVERTER_KW}
          size={110}
          strokeWidth={7}
          color={color}
          trackColor={isDark ? 'rgba(255,255,255,0.08)' : '#F0F0F2'}
          muted={muted}
        >
          <span
            className="text-lg font-bold leading-tight"
            style={{ color: muted ? '#9B9BA4' : textMain }}
          >
            {fmtKw(inv.pacKw)}
          </span>
          <span className="text-[10px] mt-0.5" style={{ color: textMuted }}>
            kW
          </span>
        </CircularGauge>
      </div>

      <div className="mt-3 text-center">
        <div className="text-sm font-semibold" style={{ color: textMain }}>
          Unit {inv.unitId}
        </div>
        <div
          className="text-xs mt-1 font-medium px-2 py-0.5 rounded-full inline-block"
          style={{
            background: inv.operationalState === 'generating' ? 'rgba(13,122,60,0.08)' : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(107,107,112,0.08)'),
            color,
          }}
        >
          {stateLabel}
        </div>
        {inv.percentOfNominal != null && !muted && (
          <div className="text-[11px] mt-1.5" style={{ color: textMuted }}>
            {fmtPct(inv.percentOfNominal)} of nominal
          </div>
        )}
      </div>
    </div>
  );
}

export default function InverterTelemetryWidget({ assetId }: Props) {
  const [data, setData] = useState<InverterTelemetryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const { isDark } = useTheme();

  const cardBg = isDark ? '#1A1A1A' : '#fff';
  const cardBorder = isDark ? 'rgba(255,255,255,0.07)' : '#E5E5E8';
  const textMain = isDark ? '#E0E0E0' : '#1A1A1A';
  const textMuted = isDark ? '#8E8E93' : '#6B6B70';
  const kpiBg = isDark ? 'rgba(255,255,255,0.03)' : '#F9F9FB';
  const innerBg = isDark ? 'rgba(255,255,255,0.03)' : '#FAFAFA';
  const innerBorder = isDark ? 'rgba(255,255,255,0.07)' : '#F0F0F2';
  const innerCardBg = isDark ? 'rgba(255,255,255,0.04)' : '#fff';
  const iconBg = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';
  const badgeMutedBg = isDark ? 'rgba(255,255,255,0.05)' : '#F0F0F2';
  const mutedText = isDark ? '#6B6B70' : '#9B9BA4';
  const pendingBg = isDark ? 'rgba(166,107,0,0.15)' : '#FFF4E6';
  const pendingText = isDark ? '#F59E0B' : '#A66B00';
  const forecastBg = isDark ? 'rgba(31,91,143,0.12)' : 'rgba(31,91,143,0.06)';
  const footerBorder = isDark ? 'rgba(255,255,255,0.05)' : '#F5F5F7';

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.get<InverterTelemetryResponse>(
        `/forecasting/assets/${assetId}/inverters/latest`
      );
      setData(result);
      setLastRefresh(new Date());
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setData(null);
        setLastRefresh(new Date());
      } else {
        setError(getApiErrorMessage(err, 'Failed to load inverter telemetry'));
      }
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, REFRESH_MS);
    return () => clearInterval(timer);
  }, [fetchData]);

  const totalPacKw = data?.totalPacKw ?? data?.totals?.totalPacKw ?? null;
  const pctOfNominal = data?.percentOfNominal ?? data?.totals?.percentOfNominal ?? null;
  const pctOfEffectiveCap = data?.percentOfEffectiveCap ?? null;
  const effectiveCapKw = data?.effectiveCapKw ?? null;
  const curtailmentPct = data?.curtailmentPct ?? null;
  const plantNominalKw = data?.nominal?.plantKw ?? 1000;
  const gaugeMax = effectiveCapKw ?? plantNominalKw;
  const gaugePct = pctOfEffectiveCap ?? pctOfNominal;
  const groups = data?.groups ?? [];
  const forecast = data?.forecast;
  const comparison = data?.comparison;
  const inverters = data?.inverters ?? [];

  const deltaColor =
    comparison?.deltaKw == null
      ? textMuted
      : comparison.deltaKw < 0
        ? '#D12229'
        : '#0D7A3C';

  return (
    <div className="rounded-xl p-5" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: 'rgba(209,34,41,0.08)' }}
          >
            <i className="ri-flashlight-line text-sm" style={{ color: '#D12229' }}></i>
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: '#D12229' }}>
              Inverter Telemetry
            </h3>
            <p className="text-xs" style={{ color: textMuted }}>
              Live Modbus readings · 30 min interval
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {lastRefresh && (
            <span className="text-[11px]" style={{ color: mutedText }}>
              Updated {fmtTime(data?.readAtLocal ?? null)}
            </span>
          )}
          <button
            onClick={fetchData}
            disabled={loading}
            className="w-7 h-7 rounded-md flex items-center justify-center cursor-pointer transition-colors"
            style={{ background: iconBg, color: textMuted }}
            title="Refresh now"
          >
            <i className={`${loading ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-xs`}></i>
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="p-6 text-center mb-4">
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: iconBg }}
          >
            <i className="ri-error-warning-line text-xl" style={{ color: '#D12229' }}></i>
          </div>
          <p className="text-sm font-medium" style={{ color: textMain }}>
            Failed to load inverter telemetry
          </p>
          <p className="text-xs mt-1" style={{ color: textMuted }}>
            {error}
          </p>
          <button
            onClick={fetchData}
            className="mt-3 px-4 py-2 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap"
            style={{ background: '#D12229', color: '#fff' }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && !data && (
        <div className="text-center py-8">
          <i className="ri-loader-4-line animate-spin text-xl" style={{ color: textMuted }}></i>
          <p className="text-xs mt-2" style={{ color: textMuted }}>
            Loading inverter telemetry...
          </p>
        </div>
      )}

      {/* Pending telemetry — Modbus not yet configured */}
      {!error && data && data.telemetryStatus === "pending" && (
        <div className="text-center py-10 px-6">
          <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: pendingBg }}>
            <i className="ri-flashlight-line text-2xl" style={{ color: pendingText }}></i>
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: textMain }}>Live Modbus telemetry not yet configured</p>
          <p className="text-xs max-w-md mx-auto" style={{ color: textMuted }}>
            {data.message || "Asset is forecast-only until site operator enables Modbus data collection."}
          </p>
          {data.forecast?.forecastCurrentKw != null && (
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg" style={{ background: forecastBg }}>
              <i className="ri-bar-chart-line text-sm" style={{ color: '#1F5B8F' }}></i>
              <span className="text-xs" style={{ color: textMuted }}>Forecast Current Interval</span>
              <span className="text-sm font-bold" style={{ color: '#1F5B8F' }}>
                {data.forecast.forecastCurrentKw.toFixed(1)} kW
              </span>
            </div>
          )}
        </div>
      )}

      {/* Data content (normal flow) */}
      {!error && data && data.telemetryStatus !== "pending" && (
        <>
          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {/* Total AC power */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Total AC Power
              </div>
              <div className="text-base font-bold" style={{ color: textMain }}>
                {fmtKw(totalPacKw)} <span className="text-xs font-normal" style={{ color: textMuted }}>kW</span>
              </div>
            </div>

            {/* Forecast */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Forecast
              </div>
              <div className="text-base font-bold" style={{ color: textMain }}>
                {forecast?.forecastCurrentKw != null ? (
                  <>
                    {fmtKw(forecast.forecastCurrentKw)}{' '}
                    <span className="text-xs font-normal" style={{ color: textMuted }}>kW</span>
                  </>
                ) : (
                  <span style={{ color: mutedText }}>No forecast</span>
                )}
              </div>
            </div>

            {/* Delta */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Delta vs Forecast
              </div>
              <div className="text-base font-bold" style={{ color: deltaColor }}>
                {fmtDelta(comparison?.deltaKw, comparison?.deltaPct)}
              </div>
            </div>

            {/* Coverage */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Coverage
              </div>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    background:
                      data.coverage === 'full'
                        ? '#0D7A3C'
                        : data.coverage === 'partial'
                          ? '#B8860B'
                          : '#D12229',
                  }}
                />
                <span className="text-sm font-semibold capitalize" style={{ color: textMain }}>
                  {data.coverage}
                </span>
              </div>
            </div>

            {/* Available inverters */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Available Inverters
              </div>
              <div className="text-base font-bold" style={{ color: textMain }}>
                {data.availableInverterCount ?? '—'}
                <span className="text-xs font-normal" style={{ color: textMuted }}> / {data.expectedInverterCount ?? '—'}</span>
              </div>
            </div>

            {/* Quality */}
            <div className="p-3 rounded-lg" style={{ background: kpiBg }}>
              <div className="text-xs mb-1" style={{ color: textMuted }}>
                Quality
              </div>
              <span
                className="text-sm font-semibold px-2 py-0.5 rounded-full capitalize"
                style={{
                  background:
                    data.qualityFlag === 'ok'
                      ? 'rgba(13,122,60,0.08)'
                      : 'rgba(209,34,41,0.08)',
                  color: data.qualityFlag === 'ok' ? '#0D7A3C' : '#D12229',
                }}
              >
                {data.qualityFlag}
              </span>
            </div>
          </div>

          {/* Large gauge + comparison section */}
          <div className="mb-6 p-5 rounded-xl" style={{ background: innerBg, border: `1px solid ${innerBorder}` }}>
            <div className="flex flex-col lg:flex-row items-center gap-6">
              {/* Large gauge */}
              <div className="flex flex-col items-center">
                <CircularGauge
                  value={totalPacKw}
                  max={gaugeMax}
                  size={200}
                  strokeWidth={10}
                  color={textMain}
                  trackColor={isDark ? 'rgba(255,255,255,0.08)' : '#F0F0F2'}
                  muted={totalPacKw == null}
                >
                  <span
                    className="text-3xl font-bold leading-tight"
                    style={{ color: totalPacKw == null ? mutedText : textMain }}
                  >
                    {fmtKw(totalPacKw)}
                  </span>
                  <span className="text-xs mt-1" style={{ color: textMuted }}>
                    kW
                  </span>
                  <span className="text-xs mt-1 font-medium" style={{ color: textMuted }}>
                    {fmtPct(gaugePct)}
                  </span>
                </CircularGauge>
                <div className="mt-3 text-sm font-semibold" style={{ color: textMain }}>
                  Total AC Power
                </div>
                {curtailmentPct != null && curtailmentPct < 100 && (
                  <div className="mt-1 px-2 py-0.5 rounded text-[11px] font-medium" style={{ background: pendingBg, color: pendingText }} title={`Effective cap ${effectiveCapKw} kW (${curtailmentPct}% of installed)`}>
                    Curtailed to {curtailmentPct}%
                  </div>
                )}
              </div>

              {/* Forecast comparison */}
              <div className="flex-1 w-full">
                <div className="space-y-3">
                  {/* Forecast row */}
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: innerCardBg, border: `1px solid ${innerBorder}` }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: 'rgba(31,91,143,0.08)' }}>
                        <i className="ri-bar-chart-line text-xs" style={{ color: '#1F5B8F' }}></i>
                      </div>
                      <span className="text-sm font-medium" style={{ color: textMain }}>Forecast Current Interval</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: '#1F5B8F' }}>
                      {forecast?.forecastCurrentKw != null ? `${fmtKw(forecast.forecastCurrentKw)} kW` : 'No forecast'}
                    </span>
                  </div>

                  {/* Delta row */}
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: innerCardBg, border: `1px solid ${innerBorder}` }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: comparison?.deltaKw != null && comparison.deltaKw < 0 ? 'rgba(209,34,41,0.08)' : 'rgba(13,122,60,0.08)' }}>
                        <i className={`${comparison?.deltaKw != null && comparison.deltaKw < 0 ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-xs`} style={{ color: deltaColor }}></i>
                      </div>
                      <span className="text-sm font-medium" style={{ color: textMain }}>Delta Actual vs Forecast</span>
                    </div>
                    <span className="text-sm font-bold" style={{ color: deltaColor }}>
                      {fmtDelta(comparison?.deltaKw, comparison?.deltaPct)}
                    </span>
                  </div>

                  {/* Coverage row */}
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: innerCardBg, border: `1px solid ${innerBorder}` }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(107,107,112,0.08)' }}>
                        <i className="ri-signal-tower-line text-xs" style={{ color: textMuted }}></i>
                      </div>
                      <span className="text-sm font-medium" style={{ color: textMain }}>Coverage</span>
                    </div>
                    <span className="text-sm font-bold capitalize" style={{ color: textMain }}>
                      {data.coverage}
                    </span>
                  </div>

                  {/* Last update row */}
                  <div className="flex items-center justify-between p-3 rounded-lg" style={{ background: innerCardBg, border: `1px solid ${innerBorder}` }}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(107,107,112,0.08)' }}>
                        <i className="ri-time-line text-xs" style={{ color: textMuted }}></i>
                      </div>
                      <span className="text-sm font-medium" style={{ color: textMain }}>Last Update</span>
                    </div>
                    <span className="text-sm font-mono" style={{ color: textMuted }}>
                      {data.readAtLocal}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Inverter groups (multi-protocol sites — shown when 2+ groups exist) */}
          {groups.length > 1 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold" style={{ color: textMain }}>
                  Inverter Groups
                </h4>
                <span className="text-xs" style={{ color: textMuted }}>
                  {groups.length} sectors
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {groups.map((g) => (
                  <InverterGroupCard key={g.key} group={g} />
                ))}
              </div>
            </div>
          )}

          {/* Inverter grid — only legacy single-group sites */}
          {groups.length <= 1 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold" style={{ color: textMain }}>
                Inverter Units
              </h4>
              <span className="text-xs" style={{ color: textMuted }}>
                {inverters.length} inverter units included in total
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {inverters.map((inv) => (
                <InverterCard key={inv.unitId} inv={inv} isDark={isDark} />
              ))}
            </div>
          </div>
          )}
        </>
      )}
    </div>
  );
}